import { normalizeFormElementType } from '@/common/utils/formElementType'
import { pruneAttachmentElementRpcFields } from '@/common/utils/fileAttachmentsUniData'
import { resolveClientDataActualFields } from '@/common/utils/resolveClientDataActualFields'
import { extractUniDataFieldFromHookResult } from '@/common/utils/treeTableGetData'
import { createSafeClientData } from '@/services/core/core.service'
import type { ExchangeData } from '@/services/action/types'

/** Хуки FILEATTACHMENTS из registry (`prepareForFormSave` + `getFullData`). */
export type FileAttachmentsPutFullDataHooks = {
  fieldName?: string
  name?: string
  elementType?: string
  getFullData?: () => unknown
  getData?: () => unknown
  prepareForFormSave?: () => void | Promise<void>
}

export function isFileAttachmentsPutFullDataElement(el: unknown): boolean {
  if (!el || typeof el !== 'object') return false
  const type = String(
    (el as FileAttachmentsPutFullDataHooks).elementType ?? normalizeFormElementType(el) ?? '',
  ).toUpperCase()
  return type === 'FILEATTACHMENTS'
}

function registryEntryFieldName(el: unknown): string {
  if (!el || typeof el !== 'object') return ''
  const rec = el as { fieldName?: unknown; name?: unknown }
  return String(rec.fieldName ?? rec.name ?? '').trim()
}

function registryKeyMatchesPutFullDataName(registryKey: string, fieldName: string): boolean {
  if (registryKey === fieldName) return true
  if (registryKey.startsWith(`${fieldName}#`)) return true
  return registryKey.includes('__row') && registryKey.endsWith(`__${fieldName}`)
}

function entryMatchesPutFullDataName(el: unknown, fieldName: string, registryKey?: string): boolean {
  const entryName = registryEntryFieldName(el)
  if (entryName === fieldName) return true
  if (entryName.includes('__row') && entryName.endsWith(`__${fieldName}`)) return true
  if (registryKey && registryKeyMatchesPutFullDataName(registryKey, fieldName)) return true
  return false
}

/**
 * FILEATTACHMENTS for `putFullData(name)`: not only `elements[name]`.
 * Registry key may be `name#id` or a Tiles row prefix; live instance may sit in elementsByKey.
 */
export function resolveFileAttachmentsPutFullDataElement(
  registries: Array<Record<string, unknown> | null | undefined>,
  fieldName: string,
): unknown {
  const name = String(fieldName ?? '').trim()
  if (!name) return undefined
  const pools = registries.filter(
    (pool): pool is Record<string, unknown> => Boolean(pool) && typeof pool === 'object',
  )

  for (const pool of pools) {
    const direct = pool[name]
    if (isFileAttachmentsPutFullDataElement(direct) && entryMatchesPutFullDataName(direct, name, name)) {
      return direct
    }
  }

  for (const pool of pools) {
    for (const [key, el] of Object.entries(pool)) {
      if (!isFileAttachmentsPutFullDataElement(el)) continue
      if (entryMatchesPutFullDataName(el, name, key)) return el
    }
  }

  return undefined
}

async function awaitMaybePromise(value: unknown): Promise<unknown> {
  if (value != null && typeof (value as { then?: unknown }).then === 'function') {
    return await (value as Promise<unknown>)
  }
  return value
}

function extractPutFullDataFields(hookData: unknown, fieldName: string): Record<string, unknown> {
  const collected: Record<string, unknown> = {}
  if (
    hookData &&
    typeof hookData === 'object' &&
    typeof (hookData as { _getAllFields?: unknown })._getAllFields === 'function'
  ) {
    const all = (hookData as { _getAllFields: () => Record<string, unknown> })._getAllFields()
    if (all && typeof all === 'object') {
      Object.assign(collected, all)
    }
  } else {
    const field = extractUniDataFieldFromHookResult(hookData, fieldName)
    if (field !== undefined) {
      collected[fieldName] = field
    }
  }
  return pruneAttachmentElementRpcFields(collected, fieldName)
}

/**
 * Перед middle executeScript: close+wait Р7 и getFullData upload для FA-полей из `putFullData(...)`.
 * Имена — из скрипта / registry, не хардкод.
 */
export async function overlayFileAttachmentsPutFullDataOnExchangeData(
  currentData: ExchangeData,
  fieldNames: readonly string[],
  elementsRegistry: Record<string, unknown> | null | undefined,
  elementsByKey?: Record<string, unknown> | null,
): Promise<ExchangeData> {
  const names = [...new Set(fieldNames.map((name) => String(name ?? '').trim()).filter(Boolean))]
  if (!names.length) {
    return currentData
  }
  const hasPool =
    (elementsRegistry && typeof elementsRegistry === 'object') ||
    (elementsByKey && typeof elementsByKey === 'object')
  if (!hasPool) {
    return currentData
  }

  const overlay: Record<string, unknown> = {}
  for (const name of names) {
    const el = resolveFileAttachmentsPutFullDataElement([elementsRegistry, elementsByKey], name)
    if (!isFileAttachmentsPutFullDataElement(el)) continue
    const hooks = el as FileAttachmentsPutFullDataHooks
    if (typeof hooks.prepareForFormSave === 'function') {
      await hooks.prepareForFormSave.call(el)
    }
    const collect =
      typeof hooks.getFullData === 'function'
        ? hooks.getFullData
        : typeof hooks.getData === 'function'
          ? hooks.getData
          : null
    if (!collect) continue
    const hookData = await awaitMaybePromise(collect.call(el))
    Object.assign(overlay, extractPutFullDataFields(hookData, name))
  }

  if (!Object.keys(overlay).length) {
    return currentData
  }

  return createSafeClientData({
    ...(currentData || {}),
    fields: {
      ...resolveClientDataActualFields(currentData),
      ...overlay,
    },
    clientData: currentData?.clientData || {},
  })
}
